'use client';

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useDebugPanel } from '@/lib/debug-panel-context';
import { shouldShowDebugTools } from '@/lib/dev-mode';
import { isDebugPanelEnabled } from '@/lib/feature-flags';
import { logger } from '@/lib/logger';
import { Bug } from 'lucide-react';

/**
 * Client component for opening debug panel from user menu
 * Must be used within DebugPanelProvider
 * 
 * React 19 Best Practices:
 * - Uses useCallback for event handler to prevent unnecessary re-renders
 * - Early return pattern for conditional rendering
 * - Hides on home page since there's no debug information for that module
 */
export function UserMenuDebugPanel() {
  const pathname = usePathname();
  const { openPanel } = useDebugPanel();
  const showDebugTools = shouldShowDebugTools();
  const debugPanelEnabled = isDebugPanelEnabled();

  // React 19: useCallback for event handlers to prevent re-renders
  // MUST be called before any early returns (Rules of Hooks)
  const handleClick = useCallback(() => {
    logger.debug('[UserMenuDebugPanel] Opening debug panel from user menu');
    openPanel();
  }, [openPanel]);

  // Don't render if debug tools shouldn't be shown
  if (!showDebugTools || !debugPanelEnabled) {
    return null;
  }

  // Hide on home page since there's no debug information for that module
  if (pathname === '/') {
    return null;
  }

  return (
    <DropdownMenuItem onClick={handleClick} className="cursor-pointer">
      <Bug className="mr-2 h-4 w-4" />
      Debug Panel
    </DropdownMenuItem>
  );
}

